import { action, autorun, computed, makeObservable, observable, toJS } from 'mobx';

import {
  type ClaimDiceAnimationState,
  MetaGamesEventType,
  type MetagamesCurrentEvent,
} from '../../dto/metagames.types';
import { postRequest } from '../../services/api';
import { ldClient, resolveFlag } from '../../services/launchDarkly';
import type { ApiResponse } from '../../services/types';
import { getErrorMessage } from '../../utils/errors';
import { devError } from '../../utils/log';
import type { CoinType } from '../cointype/CoinTypeStore';
import type { FeatureManagerDialogType } from '../feature-manager/types';
import type { LaunchGameData } from '../game/types';
import type { RootStore } from '../RootStore';
import { EnumOpenDialog } from '../ui/types';

export class TrailStore {
  private readonly rootStore: RootStore;

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore;
    makeObservable(this);

    ldClient?.on('ready', () => {
      this.resolveAndUpdateFlags();
    });

    ldClient?.on('change', (changes) => {
      if ('trail_game' in changes) {
        this.resolveAndUpdateFlags();
      }
    });

    autorun(() => {
      const isUserAuthorized = this.rootStore.authStore.isAuthenticationFlowDone && this.rootStore.authStore.authorized;
      if (isUserAuthorized) {
        this.resolveAndUpdateFlags();
      } else {
        this.setTrailFlag(false);
      }
    });
  }

  private resolveAndUpdateFlags() {
    const trailFlag = resolveFlag('trail_game', true);

    this.setTrailFlag(trailFlag);
  }

  @observable
  public trailFlag = false;

  @action
  public setTrailFlag(flag: boolean) {
    this.trailFlag = flag;
  }

  @computed
  public get trailEventByCoin() {
    if (!this.trailEvent) return;
    const { coinType } = this.rootStore.coinTypeStore;
    return this.trailEvent[coinType.toLowerCase() as 'gc' | 'sc'];
  }

  @computed
  public get currentOpenTrailEventByCoin() {
    if (!this.currentOpenTrailEvent) return;
    const { coinType } = this.rootStore.coinTypeStore;
    return this.currentOpenTrailEvent[coinType.toLowerCase() as 'gc' | 'sc'];
  }

  @observable
  public isLoadingGame = false;

  @action
  public setIsLoadingGame(loading: boolean) {
    this.isLoadingGame = loading;
  }

  @observable
  public trailEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setTrailEvent(event: MetagamesCurrentEvent | undefined) {
    this.trailEvent = event;
  }

  @observable
  public currentOpenTrailEvent: MetagamesCurrentEvent | undefined = undefined;

  @action
  public setCurrentOpenTrailEvent(event: MetagamesCurrentEvent | undefined) {
    this.currentOpenTrailEvent = event;
    this.rootStore.metagamesStore.setCurrentOpenMetagameEvent(event);
  }

  @observable
  public claimDiceAnimationState: ClaimDiceAnimationState = {
    diceAmount: 0,
    enable: false,
  };

  @action
  public setClaimDiceAnimationState(state: Partial<ClaimDiceAnimationState>) {
    this.claimDiceAnimationState = { ...this.claimDiceAnimationState, ...state };
  }

  private updateEventOptedInStatus(eventId: string, coinType: CoinType, isOptedIn: boolean) {
    if (!this.trailEvent) {
      devError('TrailStore: updateEventOptedInStatus error: trailEvent not found');
      return;
    }

    const coinTypeLower = coinType.toLowerCase() as 'gc' | 'sc';

    if (this.trailEvent.id === eventId && this.trailEvent[coinTypeLower]) {
      const updatedCurrentEvent = {
        ...this.trailEvent,
        [coinTypeLower]: {
          ...this.trailEvent[coinTypeLower],
          isOptedIn,
        },
      };

      this.setTrailEvent(updatedCurrentEvent);
    }
  }

  public async acknowledgeOptIn(eventId: string) {
    try {
      const { coinType } = this.rootStore.coinTypeStore;

      this.updateEventOptedInStatus(eventId, coinType, true);

      await this.rootStore.metagamesStore.optInToEvent(eventId, coinType);
    } catch (error) {
      devError('TrailStore: acknowledgeOptIn error', error);
    }
  }

  public startDialogs(type?: FeatureManagerDialogType): Promise<boolean> | boolean {
    if (
      this.trailEventByCoin &&
      type &&
      ['Trail', 'TrailPreMessage'].includes(type) &&
      !this.trailEventByCoin.isOptedIn
    ) {
      void this.startTrailGame();
      return true;
    }

    return false;
  }

  public async startTrailGame() {
    const { coinType: currentCoinType } = this.rootStore.coinTypeStore;

    try {
      let metagamesUrl: string | undefined;

      if (!this.trailEvent) {
        devError('TrailStore: startTrailGame error: trailEvent not found');
        return;
      }

      const eventByCoin = this.trailEvent?.[currentCoinType.toLowerCase() as 'gc' | 'sc'];

      if (!eventByCoin) {
        devError('TrailStore: startTrailGame error: event not found');
        return;
      }

      const launchGameResponse = await postRequest<ApiResponse<LaunchGameData>>('user/launch-game', {
        coin: currentCoinType,
        gameId: eventByCoin.gameId,
      });

      if (!launchGameResponse.data?.data) {
        devError('TrailStore: startTrailGame error: launchGameResponse not found');
        return;
      }

      const { metagames } = launchGameResponse.data.data;

      if (metagames) {
        const { token, path, accessToken } = metagames;
        const { gameUiFlag } = this.rootStore.gameStore;

        metagamesUrl = this.rootStore.gameUiStore.getGameUiURL(path, {
          version: gameUiFlag ? '2' : '1',
          metagamesToken: gameUiFlag ? token : undefined,
          metagamesAccessToken: gameUiFlag ? accessToken : undefined,
          monetizationEventsToken: gameUiFlag ? token : undefined,
          token: gameUiFlag ? undefined : token,
          coinType: currentCoinType,
          flags: JSON.stringify([
            { name: 'enable_features_dates', value: resolveFlag('enable_features_dates', false) },
          ]),
        });

        const metagamesEventToSet = {
          ...this.trailEvent,
          token,
          path,
          url: metagamesUrl,
          startDate: this.trailEvent.startDate,
          endDate: this.trailEvent.endDate,
        };

        this.setCurrentOpenTrailEvent(metagamesEventToSet);

        // launch() returns false when not ready, still warming, or already live — dialog stays the fallback.
        const launchedWarm = this.rootStore.metagamesWarmStore.launch('trail', 'launchTrailGame', {
          token,
          accessToken,
          metagamesEvent: toJS(this.trailEvent),
        });

        if (!launchedWarm) {
          this.rootStore.uiStore.setOpenDialog({
            dialog: EnumOpenDialog.isOpenMetagamesDialog,
            options: { type: MetaGamesEventType.TRAIL },
          });
        }
      } else {
        devError('TrailStore: startTrailGame error: metagames not found');
        this.closeGame();
      }
    } catch (error) {
      const errorMessage = getErrorMessage(error);
      devError('TrailStore: startTrailGame error', errorMessage);
    } finally {
      this.setIsLoadingGame(false);
    }
  }

  public closeGame(dialogType?: FeatureManagerDialogType) {
    // Every exit must reach this: launch() refuses while 'live', so one missed exit makes every later
    // open fall back for the rest of the session.
    if (this.rootStore.metagamesWarmStore.activeKey === 'trail') {
      this.rootStore.metagamesWarmStore.close();
    }

    this.setCurrentOpenTrailEvent(undefined);
    this.rootStore.metagamesStore.onMetagameDialogClose?.();
    this.rootStore.uiStore.setCloseDialog();
    this.rootStore.metagamesStore.clearIframeRef();

    if (dialogType) {
      this.rootStore.featureManagerStore.onDialogFinished(dialogType);
    }
  }

  public handleTrailIframeLoad(iframeRef: HTMLIFrameElement) {
    if (!iframeRef.contentWindow) {
      devError('TrailStore handleTrailIframeLoad: Iframe content window is not available');
      return;
    }

    this.rootStore.metagamesStore.setIframeRef(iframeRef);

    if (this.currentOpenTrailEvent) {
      iframeRef.contentWindow.postMessage(
        {
          type: 'newMetagamesEvent',
          payload: {
            metagamesEvent: toJS(this.currentOpenTrailEvent),
          },
        },
        import.meta.env.VITE_GAME_UI_URL,
      );
    }
  }

  public sendAnalyticsEvent(analyticsParams: any) {
    this.rootStore.metagamesStore.sendAnalyticsEvent({
      ...analyticsParams,
      feature_sub_type: 'trail',
    });
  }

  public startClaimDiceAnimation({
    diceAmount,
    onAnimationEnd,
    coinType,
  }: {
    diceAmount: number;
    onAnimationEnd?: () => Promise<void> | void;
    coinType?: CoinType;
  }) {
    this.setClaimDiceAnimationState({
      diceAmount,
      enable: true,
      coinType,
      onAnimationEnd: () => {
        this.setClaimDiceAnimationState({ enable: false });
        void onAnimationEnd?.();
      },
    });
  }

  public resetEvent() {
    this.setTrailEvent(undefined);
    this.setCurrentOpenTrailEvent(undefined);
    this.setIsLoadingGame(false);
  }
}
